import { toast } from "sonner";

// Shopify Configuration
const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'lovable-project-is9bc.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = 'db9562a1fad4126305df9eb9ea5dfd10';

// Admin API proxy URL (bypasses publication channel requirements)
const ADMIN_API_PROXY_URL = `https://irbwlurokgoklfkqcqfm.supabase.co/functions/v1/storefront-products`;

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    descriptionHtml?: string;
    handle: string;
    vendor?: string;
    productType?: string;
    tags?: string[];
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          sku?: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice?: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          image?: {
            url: string;
            altText: string | null;
          } | null;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
    metafields?: Array<{
      namespace?: string;
      key: string;
      value: string;
    } | null> | null;
  };
}

// Localization query to get available shipping countries
const LOCALIZATION_QUERY = `
  query GetAvailableCountries {
    localization {
      availableCountries {
        name
        isoCode
        currency {
          isoCode
        }
      }
    }
  }
`;

// Cart create with buyer identity for shipping rates
const CART_CREATE_FOR_RATES_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Update buyer identity on cart
const CART_BUYER_IDENTITY_UPDATE_MUTATION = `
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        id
        deliveryGroups(first: 10) {
          nodes {
            deliveryOptions {
              handle
              title
              estimatedCost {
                amount
                currencyCode
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Shipping rate interface
export interface ShopifyShippingRate {
  handle: string;
  title: string;
  price: number;
  currencyCode: string;
}

export interface ShopifyShippingResult {
  rates: ShopifyShippingRate[];
  cheapestRate: ShopifyShippingRate | null;
}

// GraphQL Queries
const STOREFRONT_PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String, $after: String) {
    products(first: $first, query: $query, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          description
          handle
          vendor
          productType
          tags
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                sku
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                image {
                  url
                  altText
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
          metafields(identifiers: [
            {namespace: "custom", key: "product_length_cm"},
            {namespace: "custom", key: "product_width_cm"},
            {namespace: "custom", key: "product_height_cm"},
            {namespace: "custom", key: "product_video"},
            {namespace: "custom", key: "product_documents"},
            {namespace: "custom", key: "title_en"},
            {namespace: "custom", key: "title_lv"},
            {namespace: "custom", key: "title_ru"},
            {namespace: "custom", key: "description_en"},
            {namespace: "custom", key: "description_lv"},
            {namespace: "custom", key: "description_ru"},
            {namespace: "global", key: "title_en"},
            {namespace: "global", key: "title_lv"},
            {namespace: "global", key: "title_ru"},
            {namespace: "global", key: "description_en"},
            {namespace: "global", key: "description_lv"},
            {namespace: "global", key: "description_ru"}
          ]) {
            namespace
            key
            value
          }
        }
      }
    }
  }
`;

// Full variant query for lazy-loading when finish filter needs more data
const SINGLE_PRODUCT_FULL_VARIANTS_QUERY = `
  query GetProductFullVariants($handle: String!) {
    productByHandle(handle: $handle) {
      id
      images(first: 15) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            sku
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            image {
              url
              altText
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
`;

// In-memory cache for single product full variant fetches
const _fullVariantsCache = new Map<string, { images: ShopifyProduct['node']['images']; variants: ShopifyProduct['node']['variants'] }>();

export async function fetchSingleProductVariants(handle: string): Promise<{ images: ShopifyProduct['node']['images']; variants: ShopifyProduct['node']['variants'] } | null> {
  const cached = _fullVariantsCache.get(handle);
  if (cached) return cached;

  try {
    const data = await storefrontApiRequest(SINGLE_PRODUCT_FULL_VARIANTS_QUERY, { handle });
    if (!data?.data?.productByHandle) return null;

    const p = data.data.productByHandle;
    const result = {
      images: p.images,
      variants: {
        edges: (p.variants?.edges || []).map((edge: any) => ({
          ...edge,
          node: { ...edge.node, availableForSale: true },
        })),
      },
    };
    _fullVariantsCache.set(handle, result);
    return result;
  } catch (e) {
    console.error('Error fetching full variants for', handle, e);
    return null;
  }
}


const STOREFRONT_PRODUCT_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      vendor
      productType
      tags
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 50) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            sku
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            image {
              url
              altText
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
      metafields(identifiers: [
        {namespace: "custom", key: "product_video"},
        {namespace: "custom", key: "product_documents"},
        {namespace: "custom", key: "range"},
        {namespace: "custom", key: "knurl"},
        {namespace: "custom", key: "plate_material"},
        {namespace: "custom", key: "switch_type"},
        {namespace: "custom", key: "package_width_cm"},
        {namespace: "custom", key: "package_height_cm"},
        {namespace: "custom", key: "package_length_cm"},
        {namespace: "custom", key: "package_depth_cm"},
        {namespace: "custom", key: "package_weight_kg"},
        {namespace: "custom", key: "min_length_cm"},
        {namespace: "custom", key: "min_width_cm"},
        {namespace: "custom", key: "min_height_cm"},
        {namespace: "custom", key: "internal_depth_cm"},
        {namespace: "custom", key: "external_depth_cm"},
        {namespace: "custom", key: "voltage"},
        {namespace: "custom", key: "amperage"},
        {namespace: "custom", key: "frequency_hz"},
        {namespace: "custom", key: "ip_rating"},
        {namespace: "custom", key: "ce_grade"},
        {namespace: "custom", key: "lumen"},
        {namespace: "custom", key: "kelvin"},
        {namespace: "custom", key: "cri"},
        {namespace: "custom", key: "max_wattage"},
        {namespace: "custom", key: "dimmable"},
        {namespace: "custom", key: "bulb_base"},
        {namespace: "custom", key: "bulb_type"},
        {namespace: "custom", key: "num_bulbs"},
        {namespace: "custom", key: "life_hours"},
        {namespace: "custom", key: "cord_length_cm"},
        {namespace: "custom", key: "projection_mm"},
        {namespace: "custom", key: "distance_between_holes_cm"},
        {namespace: "custom", key: "rose_diameter_cm"},
        {namespace: "custom", key: "bar_diameter_cm"},
        {namespace: "custom", key: "spindle_dimension_cm"},
        {namespace: "custom", key: "line_drawing"},
        {namespace: "custom", key: "line_drawing_2"},
        {namespace: "custom", key: "line_drawing_3"},
        {namespace: "custom", key: "line_drawing_4"},
        {namespace: "custom", key: "line_drawing_5"},
        {namespace: "custom", key: "line_drawing_6"},
        {namespace: "custom", key: "line_drawing_7"},
        {namespace: "custom", key: "line_drawing_8"},
        {namespace: "custom", key: "line_drawing_9"},
        {namespace: "custom", key: "line_drawing_10"},
        {namespace: "custom", key: "spec_sheet"},
        {namespace: "custom", key: "spec_sheet_2"},
        {namespace: "custom", key: "spec_sheet_3"},
        {namespace: "custom", key: "spec_sheet_4"},
        {namespace: "custom", key: "spec_sheet_5"},
        {namespace: "custom", key: "spec_sheet_6"},
        {namespace: "custom", key: "spec_sheet_7"},
        {namespace: "custom", key: "spec_sheet_8"},
        {namespace: "custom", key: "spec_sheet_9"},
        {namespace: "custom", key: "spec_sheet_10"},
        {namespace: "custom", key: "instruction_manual"},
        {namespace: "custom", key: "instruction_manual_2"},
        {namespace: "custom", key: "instruction_manual_3"},
        {namespace: "custom", key: "instruction_manual_4"},
        {namespace: "custom", key: "instruction_manual_5"},
        {namespace: "custom", key: "instruction_manual_6"},
        {namespace: "custom", key: "instruction_manual_7"},
        {namespace: "custom", key: "instruction_manual_8"},
        {namespace: "custom", key: "instruction_manual_9"},
        {namespace: "custom", key: "instruction_manual_10"},
        {namespace: "custom", key: "technical_drawing"},
        {namespace: "custom", key: "collection"},
        {namespace: "custom", key: "hygienic_certificate"},
        {namespace: "custom", key: "declaration"},
        {namespace: "custom", key: "models_3d"},
        {namespace: "custom", key: "description_en"},
        {namespace: "custom", key: "description_lv"},
        {namespace: "custom", key: "description_ru"},
        {namespace: "custom", key: "title_en"},
        {namespace: "custom", key: "title_lv"},
        {namespace: "custom", key: "title_ru"},
        {namespace: "global", key: "title_en"},
        {namespace: "global", key: "title_lv"},
        {namespace: "global", key: "title_ru"}
      ]) {
        namespace
        key
        value
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Note attribute mutation for adding delivery info
const CART_NOTE_UPDATE_MUTATION = `
  mutation cartNoteUpdate($cartId: ID!, $note: String!) {
    cartNoteUpdate(cartId: $cartId, note: $note) {
      cart {
        id
        note
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Cart attributes update for structured data
const CART_ATTRIBUTES_UPDATE_MUTATION = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        id
        attributes {
          key
          value
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Cart discount codes update mutation
const CART_DISCOUNT_CODES_UPDATE_MUTATION = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        discountCodes {
          code
          applicable
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Storefront API helper function
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (response.status === 402) {
    toast.error("Shopify: Payment required", {
      description: "Shopify API access requires an active Shopify billing plan.",
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  return data;
}

// Helper to convert Admin API product to Storefront API format
function adminProductToStorefrontFormat(p: any, metafields?: any[]): ShopifyProduct {
  const tags = typeof p.tags === 'string' ? p.tags.split(', ').filter(Boolean) : (p.tags || []);
  const mfs = metafields || p.metafields || [];
  
  return {
    node: {
      id: `gid://shopify/Product/${p.id}`,
      title: p.title || '',
      description: (p.body_html || '').replace(/<[^>]*>/g, ''),
      descriptionHtml: p.body_html || '',
      handle: p.handle || '',
      vendor: p.vendor || '',
      productType: p.product_type || '',
      tags,
      priceRange: {
        minVariantPrice: {
          amount: p.variants?.[0]?.price || '0',
          currencyCode: 'EUR',
        },
      },
      images: {
        edges: (p.images || []).map((img: any) => ({
          node: {
            url: img.src || '',
            altText: img.alt || null,
          },
        })),
      },
      variants: {
        edges: (p.variants || []).map((v: any) => ({
          node: {
            id: `gid://shopify/ProductVariant/${v.id}`,
            title: v.title || 'Default',
            sku: v.sku || '',
            price: {
              amount: v.price || '0',
              currencyCode: 'EUR',
            },
            compareAtPrice: v.compare_at_price ? {
              amount: v.compare_at_price,
              currencyCode: 'EUR',
            } : null,
            availableForSale: true,
            image: (v.image?.url || v.image?.src) ? {
              url: v.image.url || v.image.src,
              altText: v.image.altText || v.image.alt || null,
            } : null,
            selectedOptions: [
              v.option1 ? { name: p.options?.[0]?.name || 'Option 1', value: v.option1 } : null,
              v.option2 ? { name: p.options?.[1]?.name || 'Option 2', value: v.option2 } : null,
              v.option3 ? { name: p.options?.[2]?.name || 'Option 3', value: v.option3 } : null,
            ].filter(Boolean) as Array<{ name: string; value: string }>,
          },
        })),
      },
      options: (p.options || []).map((o: any) => ({
        name: o.name || '',
        values: o.values || [],
      })),
      metafields: mfs.map((m: any) => ({
        namespace: m.namespace,
        key: m.key,
        value: m.value,
      })),
    },
  };
}

function forceProductNodeAlwaysAvailable(product: ShopifyProduct['node']): ShopifyProduct['node'] {
  return {
    ...product,
    variants: {
      ...product.variants,
      edges: (product.variants?.edges || []).map((edge) => ({
        ...edge,
        node: {
          ...edge.node,
          availableForSale: true,
        },
      })),
    },
  };
}

// --- product cache: in-memory (instant, no size limit) + sessionStorage (survives reload), 5-min TTL ---
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_VERSION = 'v7';

type ProductCacheEntry = { ts: number; data: ShopifyProduct[] };
// In-memory layer: the full 10k catalog is too big for sessionStorage quota and
// JSON.parse of multi-MB strings blocks the main thread. Memory cache makes
// repeat category clicks instant within the SPA session.
const _memProductCache = new Map<string, ProductCacheEntry>();

function getCacheKey(first: number, query?: string, lite?: boolean): string {
  return `shopify_products_${CACHE_VERSION}_${lite ? 'lite_' : ''}${first}_${query || '__all__'}`;
}

function readMemCache(key: string): ShopifyProduct[] | null {
  const entry = _memProductCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    _memProductCache.delete(key);
    return null;
  }
  return entry.data;
}

function readSessionCache(key: string): ShopifyProduct[] | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts <= CACHE_TTL_MS) {
      // Promote to memory so the next read skips JSON.parse entirely
      _memProductCache.set(key, { ts, data });
      return data as ShopifyProduct[];
    }
    sessionStorage.removeItem(key);
    return null;
  } catch {
    return null;
  }
}

function getFromCache(first: number, query?: string, lite?: boolean): ShopifyProduct[] | null {
  try {
    // First check exact key: memory, then sessionStorage
    const key = getCacheKey(first, query, lite);
    const mem = readMemCache(key);
    if (mem) return mem;
    const ses = readSessionCache(key);
    if (ses) return ses;
    // Check if a larger FULL cached result exists for the same query (full superset always
    // satisfies a lite request because it has more fields, not fewer)
    if (first < 10000) {
      const bigKey = getCacheKey(10000, query, false);
      const bigMem = readMemCache(bigKey);
      if (bigMem) {
        console.log(`[cache superset] returning ${first} from ${bigMem.length} cached (mem)`);
        return bigMem.slice(0, first);
      }
      const bigSes = readSessionCache(bigKey);
      if (bigSes) {
        console.log(`[cache superset] returning ${first} from ${bigSes.length} cached`);
        return bigSes.slice(0, first);
      }
    }
    return null;
  } catch {
    return null;
  }
}

function saveToCache(first: number, query: string | undefined, data: ShopifyProduct[], lite?: boolean) {
  try {
    // Don't cache empty results — likely a transient backend hiccup
    if (!data || data.length === 0) return;
    const key = getCacheKey(first, query, lite);
    _memProductCache.set(key, { ts: Date.now(), data });
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // sessionStorage quota exceeded — ignore (memory cache already saved above)
  }
}

// Clear all cached product lists (call after admin edits like vendor/category changes)
export function clearProductCache() {
  _memProductCache.clear();
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('shopify_products_')) keys.push(k);
    }
    keys.forEach(k => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

// Warm the full-catalog cache in the background (e.g. from the homepage) so the
// first category click on /shop is served instantly from memory.
let _catalogPrefetchStarted = false;
export function prefetchFullCatalog() {
  if (_catalogPrefetchStarted) return;
  _catalogPrefetchStarted = true;
  fetchProducts(10000).catch(() => {
    _catalogPrefetchStarted = false; // allow retry on a later trigger
  });
}

// Detect if a query can be handled by Storefront API directly.
// Storefront API supports tag:, product_type:, vendor: out of the box.
// It does NOT reliably handle title:* wildcard searches — those still need Admin API.
function canUseStorefrontApi(query: string): boolean {
  if (/title:\*/.test(query)) return false;
  return true;
}

// Fetch products — prefer Storefront API (direct, no cold start) for simple queries,
// fall back to Admin API proxy for complex queries (vendor:*, title:*).
// `lite` requests a stripped-down payload (used for the fast first-paint batch on Shop
// pages). Only honored on the Admin API proxy path; Storefront API already returns a
// small enough payload.
export async function fetchProducts(
  first: number = 50,
  query?: string,
  options?: { lite?: boolean }
): Promise<ShopifyProduct[]> {
  const lite = !!options?.lite;
  // Check cache first
  const cached = getFromCache(first, query, lite);
  if (cached) {
    console.log(`[cache hit] ${cached.length} products for query: ${query || '(all)'}`);
    return cached;
  }

  try {
    // Route: Storefront API is faster (direct browser→Shopify, no edge function cold start)
    // Use it whenever the query syntax is compatible
    if (!query || canUseStorefrontApi(query)) {
      const sfResults = await fetchProductsStorefront(first, query || undefined);
      if (sfResults.length > 0) {
        saveToCache(first, query, sfResults, lite);
        return sfResults;
      }
      // If Storefront returned 0 and there's a query, try Admin API as fallback
      if (!query) return sfResults;
    }

    // Complex queries (vendor:*, title:*) → Admin API proxy
    const response = await fetch(ADMIN_API_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'list', first, query, lite }),
    });

    if (response.ok) {
      const data = await response.json();
      if (!data.error) {
        const result = (data.products || []).map((p: any) => adminProductToStorefrontFormat(p));
        saveToCache(first, query, result, lite);
        return result;
      }
    }

    // Final fallback: Storefront API plain
    const fallback = await fetchProductsStorefront(first, query || undefined);
    saveToCache(first, query, fallback, lite);
    return fallback;
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
}

// Original Storefront API fetch (fallback)
async function fetchProductsStorefront(first: number = 50, query?: string): Promise<ShopifyProduct[]> {
  try {
    const pageSizeLimit = 250;
    let results: ShopifyProduct[] = [];
    let after: string | null = null;

    while (results.length < first) {
      const pageSize = Math.min(pageSizeLimit, first - results.length);
      const data = await storefrontApiRequest(STOREFRONT_PRODUCTS_QUERY, { first: pageSize, query, after });
      if (!data) return results;

      const edges: ShopifyProduct[] = (data.data?.products?.edges || []).map((edge: ShopifyProduct) => ({
        ...edge,
        node: forceProductNodeAlwaysAvailable(edge.node),
      }));
      results = results.concat(edges);

      const pageInfo = data.data?.products?.pageInfo;
      if (!pageInfo?.hasNextPage || !pageInfo.endCursor || edges.length === 0) break;
      after = pageInfo.endCursor;
    }

    return results;
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
}

// Fetch single product by handle — prefer Storefront product data, but always merge
// document/metafield data from Admin API proxy because Storefront can omit some
// custom metafields even when they exist on the product.
function mergeProductMetafields(
  storefrontProduct: ShopifyProduct['node'],
  adminProduct: ShopifyProduct['node']
): ShopifyProduct['node'] {
  const storefrontMetafields = storefrontProduct.metafields || [];
  const adminMetafields = adminProduct.metafields || [];

  const mergedMetafields = [
    ...storefrontMetafields,
    ...adminMetafields.filter((adminMf) => {
      if (!adminMf?.key) return false;
      return !storefrontMetafields.some(
        (sfMf) => sfMf?.namespace === adminMf.namespace && sfMf?.key === adminMf.key
      );
    }),
  ];

  return forceProductNodeAlwaysAvailable({
    ...storefrontProduct,
    metafields: mergedMetafields,
  });
}

export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct['node'] | null> {
  try {
    const [sfResult, adminResult] = await Promise.all([
      fetchProductByHandleStorefront(handle),
      (async () => {
        const response = await fetch(ADMIN_API_PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'get-by-handle', handle }),
        });

        if (!response.ok) return null;

        const data = await response.json();
        if (data.error || !data.product) return null;

        return forceProductNodeAlwaysAvailable(adminProductToStorefrontFormat(data.product, data.metafields).node);
      })(),
    ]);

    if (sfResult && adminResult) {
      return mergeProductMetafields(sfResult, adminResult);
    }

    if (sfResult) return sfResult;
    if (adminResult) return adminResult;
    return null;
  } catch (error) {
    console.error('Error fetching product:', error);
    return null;
  }
}

// Original Storefront API single product fetch (fallback)
async function fetchProductByHandleStorefront(handle: string): Promise<ShopifyProduct['node'] | null> {
  try {
    const data = await storefrontApiRequest(STOREFRONT_PRODUCT_QUERY, { handle });
    if (!data) return null;
    const product = data.data?.productByHandle;
    if (!product) return null;
    return forceProductNodeAlwaysAvailable(product);
  } catch (error) {
    console.error('Error fetching product:', error);
    return null;
  }
}

// Create checkout - simple version without Omniva pre-selection
// Customer will select delivery method (parcel locker or courier) in Shopify checkout
export async function createStorefrontCheckout(
  items: Array<{ variantId: string; quantity: number }>,
  discountCode?: string
): Promise<string> {
  const lines = items.map(item => ({
    quantity: item.quantity,
    merchandiseId: item.variantId,
  }));

  const input = { lines };

  const cartData = await storefrontApiRequest(CART_CREATE_MUTATION, { input });

  if (!cartData) {
    throw new Error('Failed to create checkout');
  }

  if (cartData.data.cartCreate.userErrors.length > 0) {
    throw new Error(`Cart creation failed: ${cartData.data.cartCreate.userErrors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  const cart = cartData.data.cartCreate.cart;
  
  if (!cart.checkoutUrl) {
    throw new Error('No checkout URL returned from Shopify');
  }

  // Apply discount code if provided
  if (discountCode) {
    try {
      await storefrontApiRequest(CART_DISCOUNT_CODES_UPDATE_MUTATION, {
        cartId: cart.id,
        discountCodes: [discountCode],
      });
    } catch (error) {
      console.error('Failed to apply discount code to Shopify cart:', error);
      // Continue with checkout even if discount code fails
    }
  }

  const url = new URL(cart.checkoutUrl);
  url.searchParams.set('channel', 'online_store');
  return url.toString();
}

// Format price
export function formatPrice(amount: string, currencyCode: string): string {
  const formatter = new Intl.NumberFormat('lv-LV', {
    style: 'currency',
    currency: currencyCode,
  });
  return formatter.format(parseFloat(amount));
}

// Fetch available shipping countries from Shopify
export interface ShopifyCountry {
  name: string;
  isoCode: string;
  currency: {
    isoCode: string;
  };
}

export async function fetchAvailableCountries(): Promise<ShopifyCountry[]> {
  try {
    const data = await storefrontApiRequest(LOCALIZATION_QUERY);
    if (!data) return [];
    return data.data?.localization?.availableCountries || [];
  } catch (error) {
    console.error('Error fetching available countries:', error);
    return [];
  }
}

// Fetch shipping rates from Shopify for a given country and cart items
export async function fetchShopifyShippingRates(
  items: Array<{ variantId: string; quantity: number }>,
  countryCode: string
): Promise<ShopifyShippingResult> {
  try {
    // Create a temporary cart with the items
    const lines = items.map(item => ({
      quantity: item.quantity,
      merchandiseId: item.variantId,
    }));

    const cartData = await storefrontApiRequest(CART_CREATE_FOR_RATES_MUTATION, {
      input: { lines }
    });

    if (!cartData || cartData.data?.cartCreate?.userErrors?.length > 0) {
      console.error('Failed to create cart for shipping rates:', cartData?.data?.cartCreate?.userErrors);
      return { rates: [], cheapestRate: null };
    }

    const cartId = cartData.data.cartCreate.cart.id;

    // Update buyer identity with country to get shipping rates
    const ratesData = await storefrontApiRequest(CART_BUYER_IDENTITY_UPDATE_MUTATION, {
      cartId,
      buyerIdentity: {
        countryCode: countryCode
      }
    });

    if (!ratesData || ratesData.data?.cartBuyerIdentityUpdate?.userErrors?.length > 0) {
      console.error('Failed to update buyer identity:', ratesData?.data?.cartBuyerIdentityUpdate?.userErrors);
      return { rates: [], cheapestRate: null };
    }

    const deliveryGroups = ratesData.data.cartBuyerIdentityUpdate.cart.deliveryGroups?.nodes || [];
    const rates: ShopifyShippingRate[] = [];

    for (const group of deliveryGroups) {
      for (const option of group.deliveryOptions || []) {
        rates.push({
          handle: option.handle,
          title: option.title,
          price: parseFloat(option.estimatedCost.amount),
          currencyCode: option.estimatedCost.currencyCode,
        });
      }
    }

    // Find cheapest rate
    const cheapestRate = rates.length > 0 
      ? rates.reduce((min, rate) => rate.price < min.price ? rate : min, rates[0])
      : null;

    return { rates, cheapestRate };
  } catch (error) {
    console.error('Error fetching shipping rates:', error);
    return { rates: [], cheapestRate: null };
  }
}
